import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { adminPaymentService, UnifiedPlan } from '@/services/adminPaymentService';
import { toastHelper } from '@/utils/toastHelper';
import { RefreshCw, Search, Eye, EyeOff, Lock } from 'lucide-react';
import CreateTieredPlanModal from './CreateTieredPlanModal';

const AdminSubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<UnifiedPlan[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterIsActive, setFilterIsActive] = useState<boolean | null>(null);
  const [filterIsCurrent, setFilterIsCurrent] = useState<boolean | null>(null);
  const [filterHasSubscribers, setFilterHasSubscribers] = useState<boolean | null>(null);
  const [showHidden, setShowHidden] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await adminPaymentService.listUnifiedPlans();
      setPlans(data.plans);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTieredPlan = async (planData: any) => {
    try {
      await adminPaymentService.createMultiplePlans(planData);
      toastHelper.newMessage('success', 'Success', 'Plans created successfully');
      fetchPlans();
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to create plans');
      throw error;
    }
  };

  const handlePlanClick = (planName: string) => {
    navigate(`/admin/subscription-plans/${encodeURIComponent(planName)}`);
  };

  const handleToggleHidden = async (e: React.MouseEvent, plan: UnifiedPlan) => {
    e.stopPropagation(); // Prevent row click
    
    // Client-side validation: Cannot hide active or current plans
    if (!plan.is_hidden) {
      if (plan.is_active) {
        toastHelper.newMessage(
          'error',
          'Cannot Hide Plan',
          'Cannot hide an active plan. Please deactivate the plan first.'
        );
        return;
      }
      if (plan.is_current) {
        toastHelper.newMessage(
          'error',
          'Cannot Hide Plan',
          'Cannot hide a current plan. Please set another plan as current first.'
        );
        return;
      }
    }
    
    try {
      const newHiddenStatus = !plan.is_hidden;
      
      // Update both monthly and annual plans if they exist
      const updatePromises = [];
      if (plan.monthly_plan_id) {
        updatePromises.push(
          adminPaymentService.updatePlan(plan.monthly_plan_id, { is_hidden: newHiddenStatus })
        );
      }
      if (plan.annual_plan_id) {
        updatePromises.push(
          adminPaymentService.updatePlan(plan.annual_plan_id, { is_hidden: newHiddenStatus })
        );
      }
      
      await Promise.all(updatePromises);
      
      toastHelper.newMessage(
        'success',
        'Success',
        `Plan ${newHiddenStatus ? 'hidden' : 'unhidden'} successfully`
      );
      
      // Refresh plans
      fetchPlans();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to update plan visibility';
      toastHelper.newMessage('error', 'Error', errorMessage);
    }
  };


  // Filter plans based on search and filters
  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      // Filter by is_hidden (show hidden only if showHidden is true)
      if (!showHidden && plan.is_hidden) {
        return false;
      }
      
      // Search by name
      if (searchQuery && !plan.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      // Filter by is_active - when filterIsActive is true, show only active plans
      if (filterIsActive !== null) {
        if (filterIsActive === true && !plan.is_active) {
          return false;
        }
        if (filterIsActive === false && plan.is_active) {
          return false;
        }
      }
      
      // Filter by is_current
      if (filterIsCurrent !== null && plan.is_current !== filterIsCurrent) {
        return false;
      }
      
      // Filter by has_subscribers
      if (filterHasSubscribers !== null) {
        const hasSubs = (plan.total_subscribers || 0) > 0;
        if (filterHasSubscribers !== hasSubs) {
          return false;
        }
      }
      
      return true;
    });
  }, [plans, searchQuery, filterIsActive, filterIsCurrent, filterHasSubscribers, showHidden]);

  const getRowHighlightClass = (plan: UnifiedPlan) => {
    if (plan.is_base && plan.is_current) {
      return 'bg-gradient-to-r from-[hsl(var(--secondary))]/10 via-[hsl(var(--secondary))]/5 to-[hsl(var(--primary))]/10 !border-l-4 !border-primary';
    } else if (plan.is_base) {
      return 'bg-[hsl(var(--secondary))]/5 !border-l-4 !border-[hsl(var(--secondary))]';
    } else if (plan.is_current) {
      return 'bg-[hsl(var(--primary))]/5 !border-l-4 !border-primary';
    }
    return '';
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Subscription Plans</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">Manage subscription plans in a unified view</p>
        </div>
        <div className="flex flex-wrap gap-2 md:gap-4">
          <CreateTieredPlanModal onCreatePlan={handleCreateTieredPlan} />
          <Button variant="outline" onClick={fetchPlans} disabled={loading} size="sm" className="flex-1 md:flex-initial">
            <RefreshCw className={`h-4 w-4 md:mr-2 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter subscription plans by various criteria</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label htmlFor="search">Search by Name</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search plans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Show Hidden Toggle */}
            <div className="space-y-2">
              <Label>Show Hidden</Label>
              <div className="flex items-center h-10">
                <Button
                  variant={showHidden ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowHidden(!showHidden)}
                  className="w-full"
                >
                  {showHidden ? "Showing Hidden" : "Show Hidden"}
                </Button>
              </div>
            </div>

            {/* Is Active Filter */}
            <div className="space-y-2">
              <Label>Is Active</Label>
              <div className="flex gap-2">
                <Button
                  variant={filterIsActive === true ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterIsActive(filterIsActive === true ? null : true)}
                >
                  Active
                </Button>
                <Button
                  variant={filterIsActive === false ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterIsActive(filterIsActive === false ? null : false)}
                >
                  Inactive
                </Button>
              </div>
            </div>

            {/* Is Current Filter */}
            <div className="space-y-2">
              <Label>Is Current</Label>
              <div className="flex gap-2">
                <Button
                  variant={filterIsCurrent === true ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterIsCurrent(filterIsCurrent === true ? null : true)}
                >
                  Current
                </Button>
                <Button
                  variant={filterIsCurrent === false ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterIsCurrent(filterIsCurrent === false ? null : false)}
                >
                  Not Current
                </Button>
              </div>
            </div>

            {/* Has Subscribers Filter */}
            <div className="space-y-2">
              <Label>Has Subscribers</Label>
              <div className="flex gap-2">
                <Button
                  variant={filterHasSubscribers === true ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterHasSubscribers(filterHasSubscribers === true ? null : true)}
                >
                  Yes
                </Button>
                <Button
                  variant={filterHasSubscribers === false ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterHasSubscribers(filterHasSubscribers === false ? null : false)}
                >
                  No
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plans Table */}
      {filteredPlans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {plans.length === 0 
                ? "No plans found. Create your first plan to get started."
                : "No plans match the current filters."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            <div>
              <h2 className="text-xl font-semibold mb-1">Plans ({filteredPlans.length})</h2>
              <p className="text-sm text-muted-foreground">Tap on a card to view plan details</p>
            </div>
            {filteredPlans.map((plan) => (
              <Card
                key={plan.name}
                className={`cursor-pointer hover:bg-muted/50 transition-colors ${getRowHighlightClass(plan)}`}
                onClick={() => handlePlanClick(plan.name)}
              >
                <CardContent className="p-4 space-y-3">
                  {/* Plan Name and Badges */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">{plan.name}</h3>
                      <div className="flex gap-2 flex-wrap">
                        {plan.is_base && (
                          <Badge variant="secondary" className="bg-[hsl(var(--secondary))] text-secondary-foreground text-xs">
                            Base
                          </Badge>
                        )}
                        {plan.is_current && (
                          <Badge variant="default" className="bg-[hsl(var(--primary))] text-primary-foreground text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                    </div>
                    {plan.is_active || plan.is_current ? (
                      <div 
                        className="flex items-center justify-center h-8 w-8 rounded-md bg-muted/50 border border-muted"
                        title={
                          plan.is_active && plan.is_current
                            ? "Cannot hide: Plan is active and current"
                            : plan.is_active
                              ? "Cannot hide: Plan is active"
                              : "Cannot hide: Plan is current"
                        }
                      >
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleToggleHidden(e, plan)}
                        className="h-8 w-8 p-0 flex-shrink-0"
                        title={plan.is_hidden ? "Unhide plan" : "Hide plan"}
                      >
                        {plan.is_hidden ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Plan Details Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Currency</p>
                      <p className="font-medium">{plan.currency_code}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Status</p>
                      <Badge variant={plan.is_active ? "default" : "secondary"} className="text-xs">
                        {plan.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Monthly Tiers</p>
                      <Badge variant={plan.monthly_tiers_count > 0 ? "outline" : "secondary"} className="text-xs">
                        {plan.monthly_tiers_count}/{plan.has_all_monthly_tiers ? '✓' : '?'} Tiers
                      </Badge>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Annual Tiers</p>
                      <Badge variant={plan.annual_tiers_count > 0 ? "outline" : "secondary"} className="text-xs">
                        {plan.annual_tiers_count}/{plan.has_all_annual_tiers ? '✓' : '?'} Tiers
                      </Badge>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground text-xs mb-1">Subscribers</p>
                      <div>
                        <span className="font-medium">{plan.total_subscribers || 0}</span>
                        {plan.monthly_plan && plan.annual_plan && (
                          <span className="text-muted-foreground text-xs ml-2">
                            ({plan.monthly_subscriber_count || 0}M / {plan.annual_subscriber_count || 0}A)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <Card className="hidden md:block">
            <CardHeader>
              <CardTitle>Plans ({filteredPlans.length})</CardTitle>
              <CardDescription>Click on a row to view plan details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="min-w-[640px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[200px]">Plan Name</TableHead>
                      <TableHead className="hidden md:table-cell text-center">Currency</TableHead>
                      <TableHead className="min-w-[100px] text-center">Status</TableHead>
                      <TableHead className="hidden lg:table-cell text-center">Monthly Tiers</TableHead>
                      <TableHead className="hidden lg:table-cell text-center">Annual Tiers</TableHead>
                      <TableHead className="hidden md:table-cell min-w-[120px] text-center">Subscribers</TableHead>
                      <TableHead className="min-w-[80px] text-center">Make Hidden</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPlans.map((plan) => (
                      <TableRow
                        key={plan.name}
                        className={`cursor-pointer hover:bg-muted/50 transition-colors ${getRowHighlightClass(plan)}`}
                        onClick={() => handlePlanClick(plan.name)}
                        style={plan.is_current ? { borderLeft: '4px solid hsl(var(--primary))' } : undefined}
                      >
                        <TableCell className="font-medium">
                          <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
                            <span className="break-words">{plan.name}</span>
                            <div className="flex gap-1 flex-wrap">
                              {plan.is_base && (
                                <Badge variant="secondary" className="bg-[hsl(var(--secondary))] text-secondary-foreground text-xs">
                                  Base
                                </Badge>
                              )}
                              {plan.is_current && (
                                <Badge variant="default" className="bg-[hsl(var(--primary))] text-primary-foreground text-xs">
                                  Current
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-center">{plan.currency_code}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={plan.is_active ? "default" : "secondary"} className="text-xs">
                            {plan.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Badge 
                              variant={plan.monthly_tiers_count > 0 ? "outline" : "secondary"} 
                              className="text-xs"
                            >
                              {plan.monthly_tiers_count} Tier{plan.monthly_tiers_count !== 1 ? 's' : ''}
                            </Badge>
                            {plan.has_all_monthly_tiers && (
                              <span className="text-xs text-green-600 dark:text-green-400">✓ Complete</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Badge 
                              variant={plan.annual_tiers_count > 0 ? "outline" : "secondary"} 
                              className="text-xs"
                            >
                              {plan.annual_tiers_count} Tier{plan.annual_tiers_count !== 1 ? 's' : ''}
                            </Badge>
                            {plan.has_all_annual_tiers && (
                              <span className="text-xs text-green-600 dark:text-green-400">✓ Complete</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-medium">{plan.total_subscribers || 0}</span>
                            {(plan.monthly_tiers_count > 0 || plan.annual_tiers_count > 0) && (
                              <span className="text-muted-foreground text-xs">
                                ({plan.monthly_subscriber_count || 0}M / {plan.annual_subscriber_count || 0}A)
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center">
                            {plan.is_active || plan.is_current ? (
                              <div 
                                className="flex items-center justify-center h-8 w-8 rounded-md bg-muted/50 border border-muted"
                                title={
                                  plan.is_active && plan.is_current
                                    ? "Cannot hide: Plan is active and current"
                                    : plan.is_active
                                      ? "Cannot hide: Plan is active"
                                      : "Cannot hide: Plan is current"
                                }
                              >
                                <Lock className="h-4 w-4 text-muted-foreground" />
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleToggleHidden(e, plan)}
                                className="h-8 w-8 p-0"
                                title={plan.is_hidden ? "Unhide plan" : "Hide plan"}
                              >
                                {plan.is_hidden ? (
                                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <Eye className="h-4 w-4 text-muted-foreground" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminSubscriptionPlans;
